import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  SimpleGrid,
  Box,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { NumericFormat } from 'react-number-format';
import { Select as ChakraSelect } from 'chakra-react-select';
import { IEventoCreate, IChecklistTemplateGrupo, useEventos } from '../../../hooks/useEventos';
import { IDataNoPagination } from '../../../types/shared';
import { KanbanDatePicker } from '../../../components/kanban/KanbanDatePicker';
import { api } from '../../../services/api';

interface SelectOption {
  label: string;
  value: number;
}

interface EmpresaOption {
  label: string;
  value: number;
}

interface EventoFormProps {
  isOpen: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSave: (data: IEventoCreate) => void;
  isLoading?: boolean;
  defaultValues?: Partial<IEventoCreate>;
}

export default function EventoForm({
  isOpen,
  onClose,
  onSave,
  isLoading,
  defaultValues,
}: EventoFormProps) {
  const [nome, setNome] = useState('');
  const [artista, setArtista] = useState('');
  const [dtEvento, setDtEvento] = useState('');
  const [local, setLocal] = useState('');
  const [cidade, setCidade] = useState('');
  const [uf, setUf] = useState('');
  const [valorOrcamento, setValorOrcamento] = useState('');
  const [descricao, setDescricao] = useState('');
  const [selectedEmpresa, setSelectedEmpresa] = useState<EmpresaOption | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<SelectOption | null>(null);
  const [empresas, setEmpresas] = useState<any[]>([]);

  const { getChecklistTemplateGrupos } = useEventos();

  const { data: gruposData } = useQuery<IDataNoPagination<IChecklistTemplateGrupo[]>>({
    queryKey: ['evento-checklist-template-grupos'],
    queryFn: getChecklistTemplateGrupos,
    enabled: isOpen,
  });

  const templateOptions: SelectOption[] = useMemo(
    () =>
      (gruposData?.data ?? []).map(g => ({
        label: `${g.nome} (${g.itens.length} itens)`,
        value: g.id,
      })),
    [gruposData],
  );

  useEffect(() => {
    if (!isOpen) return;
    const fetchEmpresas = async () => {
      try {
        // Busca primeira pagina para saber total
        const first = await api.get('/api/v1/empresas/', { params: { size: 100, page: 1 } });
        const firstBody = first.data;
        const firstItems = firstBody?.data?.items || firstBody?.data || firstBody?.items || [];
        const total = firstBody?.data?.total || firstBody?.total || 0;
        const totalPages = Math.ceil(total / 100);

        if (totalPages <= 1) {
          setEmpresas(firstItems);
          return;
        }

        // Busca restante em paralelo
        const requests = Array.from({ length: totalPages - 1 }, (_, i) =>
          api.get('/api/v1/empresas/', { params: { size: 100, page: i + 2 } }),
        );
        const responses = await Promise.all(requests);
        const rest = responses.flatMap(r => {
          const b = r.data;
          return b?.data?.items || b?.data || b?.items || [];
        });
        setEmpresas([...firstItems, ...rest]);
      } catch {
        setEmpresas([]);
      }
    };
    fetchEmpresas();
  }, [isOpen]);

  const empresaOptions: EmpresaOption[] = useMemo(
    () =>
      empresas.map((emp: any) => ({
        label: `${emp.nome || emp.razao_social}${emp.cnpj ? ` (${emp.cnpj})` : ''}`,
        value: emp.id,
      })),
    [empresas],
  );

  useEffect(() => {
    if (isOpen && defaultValues) {
      setNome(defaultValues.nome || '');
      setArtista(defaultValues.artista || '');
      setDtEvento(defaultValues.dt_evento || '');
      setLocal(defaultValues.local || '');
      setCidade(defaultValues.cidade || '');
      setUf(defaultValues.uf || '');
      setValorOrcamento(String(defaultValues.valor_orcamento || ''));
      setDescricao(defaultValues.descricao || '');
      const found = empresaOptions.find(e => e.value === defaultValues.empresa_id);
      setSelectedEmpresa(found || null);
      setSelectedTemplate(null);
    } else if (isOpen) {
      setNome('');
      setArtista('');
      setDtEvento('');
      setLocal('');
      setCidade('');
      setUf('');
      setValorOrcamento('');
      setDescricao('');
      setSelectedEmpresa(null);
      setSelectedTemplate(null);
    }
  }, [isOpen, defaultValues, empresaOptions]);

  const handleSubmit = () => {
    if (!nome || !selectedEmpresa) return;
    onSave({
      nome,
      artista: artista || undefined,
      dt_evento: dtEvento || undefined,
      local: local || undefined,
      cidade: cidade || undefined,
      uf: uf || undefined,
      valor_orcamento: Number(valorOrcamento) || 0,
      descricao: descricao || undefined,
      empresa_id: selectedEmpresa.value,
      checklist_template_grupo_id: selectedTemplate?.value || undefined,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="16px" fontWeight="700">
          {defaultValues ? 'Editar Evento' : 'Novo Evento'}
        </ModalHeader>
        <ModalCloseButton borderRadius="full" />
        <ModalBody>
          <SimpleGrid columns={2} spacing={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Nome do Evento</FormLabel>
              <Input
                size="sm"
                borderRadius="lg"
                value={nome}
                onChange={e => setNome(e.target.value)}
                placeholder="Ex: Henrique & Juliano — Goiânia"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Artista</FormLabel>
              <Input
                size="sm"
                borderRadius="lg"
                value={artista}
                onChange={e => setArtista(e.target.value)}
                placeholder="Ex: Henrique & Juliano"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Empresa</FormLabel>
              <Box
                css={{
                  '.chakra-select__menu': { zIndex: 9999 },
                }}
              >
                <ChakraSelect
                  options={empresaOptions}
                  value={selectedEmpresa}
                  onChange={opt => setSelectedEmpresa(opt as EmpresaOption | null)}
                  placeholder="Buscar empresa..."
                  isSearchable
                  isClearable
                  noOptionsMessage={() => 'Nenhuma empresa encontrada'}
                  size="sm"
                  chakraStyles={{
                    control: provided => ({
                      ...provided,
                      borderRadius: '8px',
                      fontSize: '13px',
                      minHeight: '32px',
                    }),
                    menu: provided => ({
                      ...provided,
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      border: '1px solid #E2E8F0',
                      overflow: 'hidden',
                    }),
                    option: (provided, state) => {
                      let bg = 'white';
                      if (state.isSelected) bg = '#3182CE';
                      else if (state.isFocused) bg = '#EBF8FF';
                      return {
                        ...provided,
                        fontSize: '13px',
                        bg,
                        color: state.isSelected ? 'white' : '#4A5568',
                      };
                    },
                    placeholder: provided => ({
                      ...provided,
                      color: '#A0AEC0',
                      fontSize: '13px',
                    }),
                  }}
                />
              </Box>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Data do Evento</FormLabel>
              <KanbanDatePicker
                value={dtEvento}
                onChange={setDtEvento}
                placeholder="Selecionar data"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Local</FormLabel>
              <Input
                size="sm"
                borderRadius="lg"
                value={local}
                onChange={e => setLocal(e.target.value)}
                placeholder="Ex: Arena Multiuso GO"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Valor Orçamento</FormLabel>
              <Input
                as={NumericFormat}
                size="sm"
                borderRadius="lg"
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                prefix="R$ "
                value={valorOrcamento ? parseFloat(valorOrcamento) : ''}
                onValueChange={({ floatValue }) => setValorOrcamento(String(floatValue ?? ''))}
                placeholder="R$ 0,00"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Cidade</FormLabel>
              <Input
                size="sm"
                borderRadius="lg"
                value={cidade}
                onChange={e => setCidade(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">UF</FormLabel>
              <Input
                size="sm"
                borderRadius="lg"
                maxLength={2}
                value={uf}
                onChange={e => setUf(e.target.value.toUpperCase())}
              />
            </FormControl>
          </SimpleGrid>
          {!defaultValues && (
            <FormControl mt={4}>
              <FormLabel fontSize="sm">Template de Checklist</FormLabel>
              <Box css={{ '.chakra-select__menu': { zIndex: 9999 } }}>
                <ChakraSelect
                  options={templateOptions}
                  value={selectedTemplate}
                  onChange={opt => setSelectedTemplate(opt as SelectOption | null)}
                  placeholder="Selecionar template (opcional)"
                  isSearchable
                  isClearable
                  noOptionsMessage={() => 'Nenhum template encontrado'}
                  size="sm"
                  chakraStyles={{
                    control: provided => ({
                      ...provided,
                      borderRadius: '8px',
                      fontSize: '13px',
                      minHeight: '32px',
                    }),
                    menu: provided => ({
                      ...provided,
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      border: '1px solid #E2E8F0',
                      overflow: 'hidden',
                    }),
                    option: (provided, state) => {
                      let bg = 'white';
                      if (state.isSelected) bg = '#3182CE';
                      else if (state.isFocused) bg = '#EBF8FF';
                      return {
                        ...provided,
                        fontSize: '13px',
                        bg,
                        color: state.isSelected ? 'white' : '#4A5568',
                      };
                    },
                    placeholder: provided => ({
                      ...provided,
                      color: '#A0AEC0',
                      fontSize: '13px',
                    }),
                  }}
                />
              </Box>
            </FormControl>
          )}
          <FormControl mt={4}>
            <FormLabel fontSize="sm">Descrição</FormLabel>
            <Textarea
              size="sm"
              borderRadius="lg"
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              rows={3}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter pt={2}>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose} borderRadius="full">
            Cancelar
          </Button>
          <Button
            size="sm"
            bg="gray.900"
            color="white"
            borderRadius="full"
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!nome || !selectedEmpresa}
            _hover={{ bg: 'gray.800' }}
          >
            {defaultValues ? 'Salvar' : 'Criar Evento'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
